// Sir Perceval Genesis helpers. Pure. No React, no Three.js.
//
// ── Codex / rules text ──
//   "Genesis → An opponent chooses a site.
//    When Sir Perceval enters that site, draw a card, then reactivate
//    his Genesis ability."
//
// The flow has two halves the runtime wires together:
//   1. Genesis fires an OPPONENT cell-pick over every cell that holds a
//      Site. The picked cell is stamped on the source as
//      `_percevalTargetCell` (a { col, row }).
//   2. An `on_stop` entry, gated by the `source_at_remembered_cell`
//      condition, fires when Perceval's own move completes ON that cell:
//      draw a card, then re-arm by re-opening the opponent's pick.
//
// Re-arm recursion guard: after the draw, re-opening the pick must not
// instantly re-satisfy `source_at_remembered_cell`. The re-arm clears
// the remembered cell first; the runtime additionally suppresses a
// re-fire when the freshly-picked cell already equals Perceval's
// current cell (the opponent picked the cell he is standing on).

/**
 * Every cell that currently holds a Site, as "col,row" keys for a
 * cell-pick `validCells` Set. Sir Perceval's opponent picks any site
 * in the realm. Genesis doesn't constrain which.
 *
 * @param meshes host.meshes Map
 * @param cellAt (x, z) => { col, row } | null
 * @returns {Set<string>} "col,row" keys of cells holding a Site
 */
export function siteCellKeys(meshes, cellAt) {
  const out = new Set();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || !card.isSite) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    out.add(`${cell.col},${cell.row}`);
  }
  return out;
}

/**
 * The Site card instance sitting on `cell`, or null. Mirror of
 * `siteCellKeys` but resolving a single cell to its occupant. Used to
 * name the picked site in the logbook.
 *
 * @param meshes host.meshes Map
 * @param cellAt (x, z) => { col, row } | null
 * @param cell { col, row }
 * @returns {object|null} the Site's cardInstance
 */
export function siteCardAtCell(meshes, cellAt, cell) {
  if (!cell) return null;
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || !card.isSite) continue;
    const at = cellAt(mesh.position.x, mesh.position.z);
    if (at && at.col === cell.col && at.row === cell.row) return card;
  }
  return null;
}

/**
 * True iff `cell` matches the cell stamped on `sourceCard` by the
 * opponent's Genesis pick. Used by the `source_at_remembered_cell`
 * condition and by the re-arm recursion guard.
 *
 * @param sourceCard the live Perceval instance ({ _percevalTargetCell })
 * @param cell { col, row } | null. The cell to compare
 * @returns {boolean}
 */
export function cellMatchesRemembered(sourceCard, cell) {
  const remembered = sourceCard?._percevalTargetCell;
  if (!remembered || !cell) return false;
  return remembered.col === cell.col && remembered.row === cell.row;
}
